import i18next, { AVAILABLE_LANGUAGES, t } from '@/i18n'

import { getAvailableCountry } from './country'

/**
 * Interpolates a localized string (loaded with the i18nKey) to a given reference string.
 */
export const interpolate = (
  i18nKey: string,
  referenceString: string,
): string => {
  const localizedString = t(i18nKey)

  if (localizedString === i18nKey) {
    console.warn(`WARNING(astro-i18next): missing translation key ${i18nKey}.`)
    return referenceString
  }

  const tagsRegex = /<([\w\d]+)([^>]*)>/gi

  const referenceStringMatches = referenceString.match(tagsRegex)

  if (!referenceStringMatches) {
    console.warn(
      'WARNING(astro-i18next): default slot does not include any HTML tag to interpolate! You should use the `t` function directly.',
    )
    return localizedString
  }

  const referenceTags = [] as { name: string; attributes: string }[]

  referenceStringMatches.forEach((tagNode) => {
    const [, name, attributes] = tagsRegex.exec(tagNode) ?? []
    referenceTags.push({ name, attributes })

    tagsRegex.exec('')
  })

  let interpolatedString = localizedString
  for (let index = 0; index < referenceTags.length; index++) {
    const referencedTag = referenceTags[index]
    // Determine starting index based on whether reference string starts with a tag
    const startsWithTag = referenceString.trim().startsWith('<')
    const tagIndex = startsWithTag ? index * 2 : index * 2 + 1

    // Replace opening tags
    interpolatedString = interpolatedString.replaceAll(
      `<${tagIndex}>`,
      `<${referencedTag.name}${referencedTag.attributes}>`,
    )

    if (referencedTag.name === 'br') {
      return interpolatedString
    }

    // Replace closing tags
    interpolatedString = interpolatedString.replaceAll(
      `</${tagIndex}>`,
      `</${referencedTag.name}>`,
    )
  }

  return interpolatedString
}

export function getUserLocaleInfo() {
  const userCountry = getAvailableCountry()

  const userLanguage = `${i18next.language}-${userCountry}`

  const getLanguage = () => {
    if (AVAILABLE_LANGUAGES.includes(userLanguage)) {
      return userLanguage
    }

    return AVAILABLE_LANGUAGES[0]
  }

  return {
    userCountry,
    userLanguage: getLanguage(),
    shortLanguage: getLanguage().split('-')[0],
  }
}

export const getLanguageFromUrl = (lang?: string, country?: string) => {
  if (lang || country) {
    const langCountry = `${lang}-${country}`

    if (AVAILABLE_LANGUAGES.includes(langCountry)) {
      return langCountry
    }

    if (lang && AVAILABLE_LANGUAGES.includes(lang)) {
      return lang
    }
  }
}

export const getPreferredLanguage = (lang?: string, country?: string) => {
  const langFromUrl = getLanguageFromUrl(lang, country)

  if (langFromUrl) {
    return langFromUrl
  }

  const { userLanguage } = getUserLocaleInfo()

  return userLanguage
}

export function handleLanguageChange(lang?: string, country?: string) {
  const preferredLanguage = getPreferredLanguage(lang, country)

  const [language] = preferredLanguage.split('-')

  i18next.changeLanguage(preferredLanguage)

  return {
    language,
  }
}

export const getBlogLanguage = (lang?: string, country?: string) => {
  const preferredLanguage = getPreferredLanguage(lang, country)

  if (lang === 'pt' || preferredLanguage === 'pt' || country === 'BR') {
    return 'en-US'
  }

  if (lang === 'es' && country === 'MX') {
    return 'es'
  }

  if (lang === 'es' && country === 'AR') {
    return 'es-AR'
  }

  if (lang === 'es' && country === 'CO') {
    return 'es-CO'
  }

  // contentful uses en-US instead of en
  if (preferredLanguage === 'en') {
    return 'en-US'
  }

  return preferredLanguage
}

/**
 * This function differs from getBlogLanguage() in that it returns the actual pt-BR locale.
 * In Contentful, pt-BR is not available as a separate locale but rather as an additional
 * field on the schema, so we need a separate function to handle this case.
 */
export const getBlogCategoryLocale = (lang?: string, country?: string) => {
  const preferredLanguage = getPreferredLanguage(lang, country)

  if (lang === 'pt' || preferredLanguage === 'pt' || country === 'BR') {
    return 'pt-BR'
  }

  if (lang === 'es' && country === 'MX') {
    return 'es'
  }

  if (lang === 'es' && country === 'AR') {
    return 'es-AR'
  }

  if (lang === 'es' && country === 'CO') {
    return 'es-CO'
  }

  // contentful uses en-US instead of en
  if (preferredLanguage === 'en') {
    return 'en-US'
  }

  return preferredLanguage
}
